import { AfterViewInit, Component, ElementRef, Renderer2, ViewChild } from '@angular/core';
import dragVertical from '@iconify/icons-mdi/drag-vertical';
import { GestureController } from '@ionic/angular';
import { AppStateService } from '../service/app-state.service';

export enum Breakpoint {
  CLOSED = 'closed',
  SMALL = 'small',
  MEDIUM = 'medium',
  FULL = 'full'
}
export const initialDrawerState: Breakpoint = Breakpoint.SMALL;
export const DRAWER_RESIZE_EVENT = 'drawer-resize';


@Component({
  selector: 'app-slide-drawer',
  templateUrl: './slide-drawer.component.html',
  styleUrls: ['./slide-drawer.component.scss'],
})
export class SlideDrawerComponent implements AfterViewInit {
  public dragIcon = dragVertical;
  public handleWidth: number;

  public currentBreakpoint: Breakpoint = initialDrawerState;
  private breakpoints: { [key in Breakpoint]: number };

  private snapMargin = 200; // Snap threshold in pixels.
  private snapSpeed = 300;  // Snap speed in milliseconds.

  @ViewChild('contentWrapper', { read: ElementRef }) private content: ElementRef;

  constructor(
    private appState: AppStateService,
    private gestureController: GestureController,
    private hostElement: ElementRef,
    private renderer: Renderer2,
  ) { }

  ngAfterViewInit() {
    this.handleWidth = parseInt(getComputedStyle(this.hostElement.nativeElement).getPropertyValue('--handle-width'), 10);

    const hostNode = this.hostElement.nativeElement;
    const gesture = this.gestureController.create({
      gesturePriority: 30, //set higher priority to conflicts with other gestures
      el: hostNode,
      gestureName: 'drag-drawer',
      direction: 'x',
      passive: true,
      canStart: gest => (gest.event.target as HTMLElement).id === 'drawer-handle',
      onStart: _ => {
        this.renderer.setStyle(hostNode, 'transition', '');
      },
      onMove: gest => {
        let distance = this.breakpoints[this.currentBreakpoint] + gest.deltaX;
        if (distance <= this.breakpoints[Breakpoint.CLOSED]) { distance = this.breakpoints[Breakpoint.CLOSED]; }
        if (distance >= this.breakpoints[Breakpoint.FULL]) { distance = this.breakpoints[Breakpoint.FULL]; }
        gest.data = { distance };
        this.renderer.setStyle(hostNode, 'transform', `translateX(${distance}px)`);
      },
      onEnd: gest => {
        if (gest.data?.distance < this.breakpoints[Breakpoint.SMALL] - this.snapMargin) {
          this.setTo(Breakpoint.CLOSED);
        } else if (gest.data?.distance < this.breakpoints[Breakpoint.SMALL] + this.snapMargin) {
          this.setTo(Breakpoint.SMALL);
        } else if (gest.data?.distance < this.breakpoints[Breakpoint.MEDIUM] + this.snapMargin) {
          this.setTo(Breakpoint.MEDIUM);
        } else {
          this.setTo(Breakpoint.FULL);
        }
      }
    }, true);
    gesture.enable(true);
  }

  getWidth() {
    return this.breakpoints[this.currentBreakpoint];
  }

  reflow(totalWidth: number, menuWidth: number, sidebarWidth: number) {

    // On Firefox 105 handleWidth was NaN at this point, so we recalculate it
    this.handleWidth = parseInt(getComputedStyle(this.hostElement.nativeElement).getPropertyValue('--handle-width'), 10);

    const availableWidth = totalWidth - menuWidth - sidebarWidth;
    const contentWidth = availableWidth - this.handleWidth;

    this.breakpoints = {
      [Breakpoint.CLOSED]: 0,
      [Breakpoint.FULL]: contentWidth,
      [Breakpoint.MEDIUM]: contentWidth * 2 / 3,
      [Breakpoint.SMALL]: contentWidth / 3,
    };

    this.setTo(this.currentBreakpoint);
  }

  close() {
    this.setTo(Breakpoint.CLOSED);
  }

  maximize() {
    this.setTo(Breakpoint.FULL);
  }

  expand() {
    switch (this.currentBreakpoint) {
      case Breakpoint.CLOSED: this.setTo(Breakpoint.SMALL); break;
      case Breakpoint.SMALL: this.setTo(Breakpoint.MEDIUM); break;
      case Breakpoint.MEDIUM: this.setTo(Breakpoint.FULL); break;
    }
  }

  shrink() {
    switch (this.currentBreakpoint) {
      case Breakpoint.FULL: this.setTo(Breakpoint.MEDIUM); break;
      case Breakpoint.MEDIUM: this.setTo(Breakpoint.SMALL); break;
      case Breakpoint.SMALL: this.setTo(Breakpoint.CLOSED); break;
    }
  }

  setTo(bp: Breakpoint) {
    this.renderer.setStyle(this.hostElement.nativeElement, 'transition', `transform ${this.snapSpeed}ms ease-out`);
    this.renderer.setStyle(this.hostElement.nativeElement, 'transform', `translateX(${this.breakpoints[bp]}px)`);
    this.renderer.setStyle(this.content.nativeElement, 'width', `${this.breakpoints[bp]}px`);

    this.currentBreakpoint = bp;
    this.appState.setDrawerState(this.currentBreakpoint);

    // Send a window:resize event to make sub-components redraw themselves.
    dispatchEvent(new CustomEvent('resize', { detail: DRAWER_RESIZE_EVENT }));
  }
}
